import * as React from 'react';

import { DrawerContext, Grid, ModalContext } from '@notino/react-styleguide';
import {
  Roles,
  GetCatalogProductViewQuery,
} from '@notino/shared/definitions/types';

import { ContentWrapper } from '@components';
import ErrorBoundary from '@components/ErrorBoundary';
import { RenderInView } from '@components/RenderInView';
import { AdminPanel } from '@containers/AdminPanel/loadable';
import { useFeatureFlags } from '@context/featureFlags/FeatureFlagsProvider';
import useFeatureSwitches from '@hooks/useFeatureSwitches';
import { TabSwitcherProvider } from '@sharedComponents/TabSwitcher/context/TabSwitcherContext';

import { BlogArticles } from './BlogArticles';
import { Breadcrumb } from './Breadcrumb';
import { Header } from './Header';
import { HeadMeta } from './HeadMeta';
import { useDispatchProductsShowEvent } from './hooks/useDispatchProductsShowEvent';
import { useInjectBazaarvoice } from './hooks/useDynamicCatalogCollection';
import { useAddToLastVisited } from './hooks/useLastVisited/useAddToLastVisited';
import { useModifaceFns } from './hooks/useModiface';
import { useModiface } from './hooks/useModifaceVariants';
import { useNewPdDesignEnabled } from './hooks/useNewPdDesignEnabled';
import { useRegisterNotinoApi } from './hooks/useRegisterNotinoApi';
import { useTrackItemAndReload } from './hooks/useTrackItemAndReload';
import { useTrackPageView } from './hooks/useTrackPageView';
import { useTrackProductUrlChange } from './hooks/useTrackProductUrlChange';
import { LastVisitedProducts } from './LastVisitedProducts';
import { TabIds } from './model';
import { ProductBaseInfo } from './ProductBaseInfo';
import { DeliveriesDisplayMode } from './ProductBaseInfo/DeliveryTime';
import MobileHeaderPlaceholder from './ProductBaseInfo/MobileHeaderPlaceholder';
import {
  ProductDetailContextProvider,
  useProductDetailContext,
} from './ProductDetailContext';
import { ProductImageGallery } from './ProductImageGallery';
import { BrandLogo } from './ProductImageGallery/BrandLogo';
import { ProductJsonLd } from './ProductJsonLd';
import { ProductPictogram } from './ProductPictogram';
import { ProductTabContent } from './ProductTabContent';
import MobileTabContentPlaceholder, {
  useMoveTabsToMobilePlaceholder,
} from './ProductTabContent/MobileTabContentPlaceholder';
import { TabsWithExpandableTextProvider } from './ProductTabContent/ProductTabContentProvider';
import { RoutineOrRelatedProducts } from './RoutineOrRelatedProducts';
import { StickySidebar } from './StickySidebar';
import {
  GalleryWrapper,
  MainInfoWrapper,
  TopSection,
  ProductTabContentGrid,
  PDViewPort,
  ShowOnMobile,
  ShowOnDesktop,
  AbsoluteHeaderWrapper,
  AbsoluteTabsWrapper,
} from './styled';
import { SwogoScript } from './SwogoScript';
import { getVariantDescription, isDiorBrand, isGiftAvailable } from './utils';
import { VariantName } from './VariantName';

interface IPDProps {
  PDView: GetCatalogProductViewQuery;
  rcKey: string;
  customRcKey: string;
  customVersionKey: string;
}

const tabsWithExpandableText = [TabIds.Description];

const ProductDetailComponent = ({
  rcKey,
  customRcKey,
  PDView: {
    productDetailByCatalogMasterId: product,
    currentCurrency,
    currentLocale,
    Settings: { imageDomainPath, deliveriesDisplayMode },
    user,
  },
  customVersionKey,
}: IPDProps) => {
  const { modifaceDesktopEnabled, modifaceMobileEnabled, reviewsOnServerIds } =
    useFeatureSwitches();
  const { swogoIntegrationEnabled } = useFeatureFlags();
  const newPdDesignEnabled = useNewPdDesignEnabled();

  const headerRef = React.useRef<HTMLDivElement>();
  const galleryWrapperRef = React.useRef<HTMLDivElement>();

  const { absoluteTabsWrapperRef, tabsPlaceholderRef, topSectionRef } =
    useMoveTabsToMobilePlaceholder();

  const {
    engravingAvailable,
    tryItFirstAvailable,
    currentVariant: variant,
  } = useProductDetailContext();

  const modiface = useModiface(product.variants);

  const {
    detectWebcamAndTrackProductDetailView,
    handleOpenModifaceModal,
    hasWebcam,
  } = useModifaceFns(product, variant, modiface);

  const giftAvailable = isGiftAvailable(product.campaigns);

  useInjectBazaarvoice(product);
  useDispatchProductsShowEvent(product.variants, {
    section: 'product_detail',
  });
  useAddToLastVisited(product);

  useTrackPageView(variant);
  useTrackItemAndReload({
    hasWebcam,
    modiface,
    giftAvailable,
    detectWebcamAndTrackProductDetailView,
  });

  useRegisterNotinoApi(variant.productCode);

  useTrackProductUrlChange(product, variant);

  const hasModiface =
    Boolean(modiface.makeup[variant.webId]) ||
    Boolean(modiface.hair[variant.webId]);

  const tabsComponent = (
    <AbsoluteTabsWrapper
      newDesign={newPdDesignEnabled}
      ref={absoluteTabsWrapperRef}
    >
      <ProductTabContentGrid
        fluid={true}
        newPdDesignEnabled={newPdDesignEnabled}
      >
        <TabsWithExpandableTextProvider
          tabsWithExpandableText={tabsWithExpandableText}
        >
          <ProductTabContent
            reviewsOnServerIds={reviewsOnServerIds}
            product={product}
            variant={variant}
            rcKey={rcKey}
            customRcKey={customRcKey}
            customVersionKey={customVersionKey}
          />
        </TabsWithExpandableTextProvider>
      </ProductTabContentGrid>
    </AbsoluteTabsWrapper>
  );

  return (
    <>
      <ErrorBoundary>
        <HeadMeta product={product} variant={variant} />
      </ErrorBoundary>
      <ErrorBoundary>
        <PDViewPort
          newPdDesignEnabled={newPdDesignEnabled}
          // This attribute needs to be here due to exponea recommendations
          data-category={product.category}
          // This  attribute needs to be here due to deloitte
          data-product-code={variant.productCode}
        >
          <ProductJsonLd
            product={product}
            selectedVariant={variant}
            imageDomainPath={imageDomainPath}
          />

          {user.role === Roles.Admin && (
            <ErrorBoundary>
              <AdminPanel
                productId={product.webMasterId}
                productUrl={product.url}
              />
            </ErrorBoundary>
          )}

          <ContentWrapper isWider={newPdDesignEnabled}>
            <Grid fluid={true}>
              <TopSection newPdDesign={newPdDesignEnabled} ref={topSectionRef}>
                <GalleryWrapper
                  newPdDesignEnabled={newPdDesignEnabled}
                  ref={galleryWrapperRef}
                >
                  <BrandLogo
                    brandId={product.brand?.id}
                    brandUrl={product.brand?.url}
                  />
                  <ShowOnMobile newPdDesign={newPdDesignEnabled}>
                    <Breadcrumb items={product.breadcrumb} />
                  </ShowOnMobile>

                  <MobileHeaderPlaceholder
                    newDesign={newPdDesignEnabled}
                    headerContainerEl={headerRef.current}
                  />

                  <ProductImageGallery
                    key={variant.webId} // NOTE: reset gallery to first image when variant changes
                    media={variant.media}
                    domain={imageDomainPath}
                    modifaceMobileEnabled={hasModiface && modifaceMobileEnabled}
                    modifaceDesktopEnabled={
                      hasModiface && modifaceDesktopEnabled
                    }
                    openModifaceModal={handleOpenModifaceModal}
                    isBrandLogoVisible={isDiorBrand(product.brand?.id)}
                    pictogram={
                      <ProductPictogram attributes={variant.attributes} />
                    }
                  />

                  {newPdDesignEnabled && tabsComponent}
                </GalleryWrapper>

                <MainInfoWrapper newPdDesignEnabled={newPdDesignEnabled}>
                  <StickySidebar galleryWrapperRef={galleryWrapperRef}>
                    <ShowOnDesktop newPdDesign={newPdDesignEnabled}>
                      <Breadcrumb
                        items={product.breadcrumb}
                        renderJsonLd={false}
                      />
                    </ShowOnDesktop>

                    <AbsoluteHeaderWrapper
                      newDesign={newPdDesignEnabled}
                      ref={headerRef}
                      isLogoForBrandAvailable={isDiorBrand(product.brand?.id)}
                    >
                      <Header
                        reviewsId="productBaseInfo"
                        productId={product.webMasterId}
                        catalogId={product.catalogMasterId}
                        productCode={product.masterProductCode}
                        collection={product.collection}
                        subName={product.subName}
                        brandInfo={product.brand}
                        desc={getVariantDescription(variant, product)}
                      />
                    </AbsoluteHeaderWrapper>

                    <ProductBaseInfo
                      brand={product.brand.name}
                      selectedVariant={variant}
                      product={product}
                      variants={product.variants}
                      currency={currentCurrency}
                      locale={currentLocale}
                      imageDomainPath={imageDomainPath}
                      deliveriesDisplayMode={
                        deliveriesDisplayMode as DeliveriesDisplayMode
                      }
                      shadefinderAvailable={
                        product.features.isShadeFinderAvailable
                      }
                      variantName={<VariantName variant={variant} />}
                      engravingAvailable={engravingAvailable}
                      tryItFirstAvailable={tryItFirstAvailable}
                      giftAvailable={giftAvailable}
                      openModifaceModal={handleOpenModifaceModal}
                      modifaceVariants={modiface.makeup}
                      hasModiface={hasModiface}
                    />
                  </StickySidebar>
                </MainInfoWrapper>
              </TopSection>
            </Grid>

            {swogoIntegrationEnabled && (
              <ErrorBoundary>
                <SwogoScript />
              </ErrorBoundary>
            )}

            {!newPdDesignEnabled && tabsComponent}

            {newPdDesignEnabled && (
              <MobileTabContentPlaceholder
                ref={tabsPlaceholderRef}
                tabsAbsoluteContainerEl={absoluteTabsWrapperRef.current}
              />
            )}

            <Grid fluid={true}>
              <ErrorBoundary>
                <RenderInView>
                  <RoutineOrRelatedProducts
                    productId={product.webMasterId}
                    productCode={product.masterProductCode}
                    relatedProducts={product.related.map((id) => String(id))}
                  />
                </RenderInView>
              </ErrorBoundary>

              <ErrorBoundary>
                <RenderInView>
                  <LastVisitedProducts
                    productId={product.webMasterId}
                    variants={product.variants}
                  />
                </RenderInView>
              </ErrorBoundary>

              <ErrorBoundary>
                <RenderInView>
                  <BlogArticles
                    categorization={product.categorization}
                    brandId={product.brand?.id}
                    masterProductCode={product.masterProductCode}
                    allProductCodes={product.variants.map((v) => v.productCode)}
                  />
                </RenderInView>
              </ErrorBoundary>
            </Grid>
          </ContentWrapper>
        </PDViewPort>
      </ErrorBoundary>
    </>
  );
};

export const ProductDetail = (props: IPDProps) => (
  <ProductDetailContextProvider
    product={props.PDView.productDetailByCatalogMasterId}
  >
    <TabSwitcherProvider>
      <ModalContext.Provider>
        <DrawerContext.Provider>
          <ProductDetailComponent {...props} />
        </DrawerContext.Provider>
      </ModalContext.Provider>
    </TabSwitcherProvider>
  </ProductDetailContextProvider>
);
